import { Component, effect } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { environment } from '../../environments/environment';
import { User } from '../core/models/common/user.model';
import { CurrentPlan } from '../core/models/corporate/current-plan.model';
import { CorporateService } from '../core/services/corporate.service';
import { EmailLauncherService } from '../core/services/email-launcher.service';
import { UserService } from '../core/services/user.service';
import { CommonSubjectsService } from '../core/signals/common-subjects.service';
import { DASHBOARD_TITLES } from '../shared/constants/dashboard-titles';

@Component({
  selector: 'app-dashboard-layout',
  standalone: false,
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss'
})
export class DashboardComponent {

  dashboardTitles = DASHBOARD_TITLES;

  currentUser: User | null = null;
  currentPlan: CurrentPlan | null = null;

  // Clave de ruta (main/drivers/...) para traducir el título del header vía
  // 'titles.<key>'; el sufijo lleva el saludo con el nombre en la pantalla main.
  currentTitleKey: string | null = null;
  titleUserSuffix = '';
  activeMenu = 'main';

  alreadyRedirected: boolean | undefined;
  environment = environment;
  isDebugBar: boolean | undefined;
  showDebugBarButton = false;

  get subscriptionActive(): boolean {
    return this.userService.hasActivePlan();
  }

  constructor(
    private userService: UserService,
    private router: Router,
    private commonSubjectsService: CommonSubjectsService,
    private corporateService: CorporateService,
    private emailLauncher: EmailLauncherService,
    private transloco: TranslocoService
  ) {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const url = event.urlAfterRedirects.split('/');
        const key = url[url.length - 1] || 'main';
        this.currentTitleKey = DASHBOARD_TITLES[key] ? key : null;
        this.titleUserSuffix = '';
        // Repuestos and Salud tienen sub-rutas internas (repuestos:
        // catalogo/configurar/seguimiento; salud: /salud/:vehicleId) —
        // mantener el ítem del sidebar resaltado anclando en el segmento
        // de sección, no en el último segmento de la URL.
        this.activeMenu =
          url[2] === 'repuestos' ? 'repuestos' : url[2] === 'salud' ? 'salud' : key;
      });

    effect(() => {
      this.currentUser = this.userService.currentUser();
      this.currentPlan = this.corporateService.currentSubscription();

      this.alreadyRedirected = this.commonSubjectsService.alreadyRedirected();
      this.isDebugBar = this.commonSubjectsService.isDebugBar();
      if (!this.currentUser) {
        this.userService.loadUserInfo().subscribe();
      }
      if (!this.alreadyRedirected) {
        this.redirectInDashboard();
      }
      if (this.isDebugBar) {
        this.showDebugBarButton = true;
        this.isDebugBar = false;
      }
      if (!this.currentPlan && this.currentUser) {
        this.corporateService.loadCurrentSubscription().subscribe();
      }
      if (this.currentUser && this.currentTitleKey === 'main') {
        const userFullName = `${this.currentUser.name ?? ''} ${this.currentUser.lastname ?? ''}`.trim();
        this.titleUserSuffix = userFullName ? `, ${userFullName}` : '';
      }
    });
  }

  redirectInDashboard() {
    const isAtRoot = this.router.url === '/dashboard' || this.router.url === '/dashboard/';
    if (this.currentUser && isAtRoot && !this.alreadyRedirected) {
      this.commonSubjectsService.setAlreadyRedirected(true);
      if (this.userService.hasActivePlan()) {
        this.router.navigate(['/dashboard', 'main']);
      } else {
        this.router.navigate(['/dashboard', 'plan']);
      }
    }
  }

  goTo(path: string[]) {
    this.activeMenu = path[path.length - 1];
    this.router.navigate(['/dashboard', ...path]);
  }

  hideDebugBar() {
    this.isDebugBar = !this.isDebugBar;
    let idDebugBar = document.getElementById('krtoolbar');
    if (idDebugBar) {
      idDebugBar.style.display = this.isDebugBar ? 'none' : 'block';
    }
  }

  ngAfterViewInit() {
    this.updateDebugBarButton();
  }

  updateDebugBarButton() {
    this.showDebugBarButton = !!document.getElementById('krtoolbar');
  }

  setTheme(mode: 'light' | 'dark') {
    const rootElement = document.querySelector('html') as HTMLElement;
    const body = document.body;
    const primeNgStylesheet = document.getElementById('primeNgThemeStylesheet') as HTMLLinkElement;

    if (mode === 'dark') {
      body.classList.add('dark-theme');
      body.classList.add('dark');
      primeNgStylesheet.href = 'assets/themes/theme-dark.css';
      rootElement.classList.add('dark-theme');
      rootElement.classList.add('dark');
      document.body.setAttribute('data-theme', 'dark');
    } else {
      body.classList.remove('dark-theme');
      body.classList.remove('dark');
      primeNgStylesheet.href = 'assets/themes/theme-light.css';
      rootElement.classList.remove('dark-theme');
      rootElement.classList.remove('dark');
      document.body.setAttribute('data-theme', 'light');
    }
  }

  toggleDarkMode() {
    // const currentTheme = document.body.getAttribute('data-theme');
    // document.body.setAttribute('data-theme', currentTheme === 'dark' ? 'light' : 'dark');
    // document.body.classList.toggle('dark-theme');
    this.setTheme(document.body.getAttribute('data-theme') === 'dark' ? 'light' : 'dark');
  }

  openHelpEmail() {
    this.emailLauncher.openEmail(
      'servicioalcliente@pitts.app',
      this.transloco.translate('quickMenu.helpEmailSubject'),
      this.transloco.translate('quickMenu.helpEmailBody')
    );
  }
}
